import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider as RadixToastProvider,
  ToastTitle,
  ToastViewport,
} from '@/components/ui/toast';

/**
 * Sistema de notificaciones tipo toast — basado en Radix Toast con un hook
 * minimalista que cualquier componente puede invocar:
 *
 *   const { showToast } = useToast();
 *   showToast({ kind: 'success', title: 'Consentimiento firmado' });
 *
 * Decisiones:
 *   - Un único Viewport en el árbol (debajo-derecha). Múltiples toasts apilan.
 *   - 4 segundos por defecto, configurable por toast.
 *   - 3 tipos: `success` (verde), `danger` (rojo), `info` (azul/neutral).
 *   - El hook expone `showToast` estable (referencialmente) para usarse
 *     dentro de `onSuccess` de React Query sin disparar renders extra.
 */

export type ToastKind = 'success' | 'danger' | 'info';

export interface ShowToastInput {
  title: string;
  description?: string;
  kind?: ToastKind;
  /** Duración en ms (default 4000). */
  durationMs?: number;
}

interface ToastItem extends Required<Omit<ShowToastInput, 'description'>> {
  id: number;
  description?: string;
}

interface ToastContextValue {
  showToast(input: ShowToastInput): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function AppToastProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([]);
  const counterRef = useRef<number>(0);

  const showToast = useCallback((input: ShowToastInput) => {
    counterRef.current += 1;
    const id = counterRef.current;
    setItems((prev) => [
      ...prev,
      {
        id,
        title: input.title,
        description: input.description,
        kind: input.kind ?? 'info',
        durationMs: input.durationMs ?? 4000,
      },
    ]);
  }, []);

  const handleOpenChange = (id: number, open: boolean): void => {
    if (!open) {
      setItems((prev) => prev.filter((it) => it.id !== id));
    }
  };

  const value = useMemo<ToastContextValue>(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      <RadixToastProvider swipeDirection="right">
        {children}
        {items.map((item) => (
          <Toast
            key={item.id}
            variant={item.kind === 'info' ? 'default' : item.kind}
            duration={item.durationMs}
            onOpenChange={(open) => handleOpenChange(item.id, open)}
          >
            <div className="flex items-start gap-2.5 flex-1 min-w-0">
              <ToastIcon kind={item.kind} />
              <div className="flex-1 min-w-0">
                <ToastTitle>{item.title}</ToastTitle>
                {item.description !== undefined && (
                  <ToastDescription>{item.description}</ToastDescription>
                )}
              </div>
            </div>
            <ToastClose />
          </Toast>
        ))}
        <ToastViewport />
      </RadixToastProvider>
    </ToastContext.Provider>
  );
}

function ToastIcon({ kind }: { kind: ToastKind }): JSX.Element {
  if (kind === 'success') {
    return <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-success" />;
  }
  if (kind === 'danger') {
    return <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-danger" />;
  }
  return <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />;
}

/**
 * Hook de acceso al sistema de toasts. Tira un error claro si se usa fuera del
 * provider — debe vivir en el root del árbol React (envuelve a `<App/>`).
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error('useToast debe usarse dentro de <AppToastProvider>');
  }
  return ctx;
}
